/**
 * @fileoverview NW Subscribe block: syncs variant + selling plan with the product cart form.
 *
 * Architecture:
 * - {@link NwSubscribeBlockUtils} — pure helpers (money, save %, form lookup). Safe to unit test.
 * - {@link NwSubscribeBlock} — one instance per [data-nw-subscribe-root]; owns DOM and event wiring.
 *
 * Extending: subclass `NwSubscribeBlock`, override hooks like `afterSync()` (add empty hook methods),
 * or replace `bootstrapNwSubscribeBlocks` in a later asset.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Constants
  // ---------------------------------------------------------------------------

  /** @enum {string} */
  const PurchaseMode = {
    ONE_TIME: 'one-time',
    SUBSCRIPTION: 'subscription',
  };

  const SELECTOR = {
    root: '[data-nw-subscribe-root]',
    configScript: 'script[data-nw-subscribe-config]',
    frequencySection: '[data-nw-frequency-section]',
    frequencySelect: '[data-nw-frequency-select]',
    /** One purchase-type group per block (name includes section id) */
    purchaseTypeInput: 'input[name^="nw-purchase-type-"]',
    variantRadio: '.nw-subscribe-block-item-variants input[type="radio"]',
    oneTimeInput: '.nw-subscribe-block-item-purchase-type input[value="one-time"]',
    subscriptionInput: '.nw-subscribe-block-item-purchase-type input[value="subscription"]',
    variantItem: '.nw-subscribe-block-item-variants-item',
  };

  // ---------------------------------------------------------------------------
  // Utilities (pure / side-effect free where noted)
  // ---------------------------------------------------------------------------

  const NwSubscribeBlockUtils = {
    /**
     * @param {number|string} cents
     * @param {string} [moneyFormat]
     * @returns {string}
     */
    formatMoney(cents, moneyFormat) {
      if (cents == null || Number.isNaN(Number(cents))) return '';
      const format = moneyFormat || '${{amount}}';
      const n = Math.max(0, Math.round(Number(cents)));
      const value = (n / 100).toFixed(2);
      const valueNoDecimals = String(Math.max(0, Math.round(n / 100)));
      return format
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/gi, valueNoDecimals)
        .replace(/\{\{\s*amount\s*\}\}/gi, value);
    },

    /**
     * @param {string} mode PurchaseMode.ONE_TIME | PurchaseMode.SUBSCRIPTION
     * @param {object} variant
     * @param {object|null} plan
     * @returns {{ show: boolean, text: string }}
     */
    getSaveState(mode, variant, plan) {
      if (mode === PurchaseMode.ONE_TIME) {
        if (!variant.compare_at_price || variant.compare_at_price <= variant.price) {
          return { show: false, text: '' };
        }
        const pct = Math.round(
          ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
        );
        return { show: pct > 0, text: `Save ${pct}%` };
      }
      if (!plan) return { show: false, text: '' };
      const subPrice = plan.price;
      const retail = variant.compare_at_price ? variant.compare_at_price : variant.price;
      if (retail > subPrice) {
        const pct = Math.round(((retail - subPrice) / retail) * 100);
        return { show: pct > 0, text: `Save ${pct}%` };
      }
      return { show: false, text: '' };
    },

    /**
     * Map pack wording in variant title to preferred subscription interval (months).
     * @param {string} title E.g. "Single Pack", "Triple Pack"
     * @returns {1 | 3 | 6 | 9 | null}
     */
    inferMonthIntervalFromVariantTitle(title) {
      if (!title || typeof title !== 'string') return null;
      const t = title.toLowerCase();
      if (/\bsingle\b/.test(t)) return 1;
      if (/\btriple\b/.test(t)) return 3;
      if (/\bsix\b/.test(t)) return 6;
      if (/\bnine\b/.test(t)) return 9;
      return null;
    },

    /**
     * Parses selling plan labels (Shopify subscriptions) into a numeric month interval.
     * @param {string} planName
     * @returns {number|null}
     */
    extractMonthsIntervalFromPlanName(planName) {
      if (!planName || typeof planName !== 'string') return null;
      const s = planName.trim().toLowerCase();
      if (/\bmonthly\b|\bevery\s+month\b/.test(s)) return 1;
      const digit = s.match(/\b(\d{1,2})\s*[–\-/]?\s*(?:months?|mos\b)/);
      if (digit) return parseInt(digit[1], 10);
      return null;
    },

    /**
     * Finds the selling-plan option whose name best matches {@code targetMonths} (e.g. 3 → "Every 3 months").
     * @param {{ id: *, name: string }[]} plans
     * @param {number|null} targetMonths
     * @returns {number} option index into {@code plans}, or {@code -1}
     */
    findSellingPlanIndexForMonths(plans, targetMonths) {
      if (!plans?.length || targetMonths == null || Number.isNaN(Number(targetMonths))) return -1;
      const target = Number(targetMonths);
      for (let i = 0; i < plans.length; i++) {
        const inferred = this.extractMonthsIntervalFromPlanName(plans[i].name);
        if (inferred === target) return i;
      }
      const wordRe = new RegExp(
        `(?:^|[^0-9])${target}\\s*[–\\-/.]?\\s*(?:months?|mos\\b)`,
        'i'
      );
      for (let i = 0; i < plans.length; i++) {
        if (wordRe.test(plans[i].name)) return i;
      }
      return -1;
    },

    /**
     * Find the add-to-cart form in the same product column as the block.
     * @param {HTMLElement} root
     * @returns {HTMLFormElement|null}
     */
    findAddToCartForm(root) {
      const productInfo = root.closest('product-info');
      const container = root.closest('.product__info-container') || productInfo;
      if (!container) return null;
      const productForm = container.querySelector('product-form.product-form');
      if (!productForm) return null;
      return (
        productForm.querySelector('form[data-type="add-to-cart-form"]') ||
        productForm.querySelector('form[action*="cart/add"]') ||
        null
      );
    },

    /**
     * @param {HTMLFormElement} form
     * @returns {HTMLInputElement}
     */
    ensureSellingPlanInput(form) {
      let el = form.querySelector('input[name="selling_plan"]');
      if (!el) {
        el = document.createElement('input');
        el.type = 'hidden';
        el.name = 'selling_plan';
        form.appendChild(el);
      }
      return el;
    },

    /**
     * @param {HTMLElement} root
     * @returns {object|null}
     */
    parseConfig(root) {
      const sc = root.querySelector(SELECTOR.configScript);
      if (!sc || !sc.textContent.trim()) return null;
      try {
        return JSON.parse(sc.textContent);
      } catch (e) {
        console.warn('[NwSubscribeBlock] Invalid JSON in data-nw-subscribe-config', e);
        return null;
      }
    },
  };

  // ---------------------------------------------------------------------------
  // Block controller
  // ---------------------------------------------------------------------------

  class NwSubscribeBlock {
    /**
     * @param {HTMLElement} root — element with [data-nw-subscribe-root]
     */
    constructor(root) {
      if (!root || !root.getAttribute) {
        throw new TypeError('NwSubscribeBlock: root element required');
      }
      this.root = root;
      /** @type {object|null} */
      this._config = null;
      /** @type {HTMLFormElement|null} */
      this._form = null;
      /** @type {HTMLInputElement|null} */
      this._variantIdInput = null;
      /** @type {HTMLInputElement|null} */
      this._sellingPlanInput = null;
      /** @type {HTMLElement|null} */
      this._frequencySection = null;
      /** @type {HTMLSelectElement|null} */
      this._frequencySelect = null;
      /** @type {NodeListOf<HTMLInputElement>|null} */
      this._purchaseInputs = null;
      /** @type {HTMLInputElement|null} */
      this._subscriptionPurchaseInput = null;
      /** @type {NodeListOf<Element>|null} */
      this._variantItems = null;

      this._onVariantIdInputChange = this._onVariantIdInputChange.bind(this);
      this._onPurchaseTypeChange = this._onPurchaseTypeChange.bind(this);
      this._onVariantRadioChange = this._onVariantRadioChange.bind(this);
      this._onFrequencyChange = this._onFrequencyChange.bind(this);

      this._isInitialized = false;
      /** True if the product has any selling plan on any variant (matches Liquid `nw_has_selling_plans`). */
      this._productLevelHasSellingPlans = root.getAttribute('data-has-selling-plans') === 'true';
    }

    /**
     * Read JSON, wire form, bind events, run first sync. Idempotent: second call no-ops.
     */
    init() {
      if (this._isInitialized) return this;

      this._config = NwSubscribeBlockUtils.parseConfig(this.root);
      if (!this._config || !this._config.variants) return this;

      this._form = NwSubscribeBlockUtils.findAddToCartForm(this.root);
      this._variantIdInput = this._form && this._form.querySelector('input[name="id"]');
      if (!this._form || !this._variantIdInput) return this;

      this._sellingPlanInput = NwSubscribeBlockUtils.ensureSellingPlanInput(this._form);
      this._frequencySection = this.root.querySelector(SELECTOR.frequencySection);
      this._frequencySelect = this.root.querySelector(SELECTOR.frequencySelect);
      this._purchaseInputs = this.root.querySelectorAll(SELECTOR.purchaseTypeInput);
      this._subscriptionPurchaseInput = this.root.querySelector(SELECTOR.subscriptionInput);
      this._variantItems = this.root.querySelectorAll(SELECTOR.variantItem);

      this._bindEvents();
      this.root.setAttribute('data-purchase-type', PurchaseMode.ONE_TIME);
      this._syncAll();
      this._isInitialized = true;
      this._afterInit();
      return this;
    }

    /**
     * Detach listeners (e.g. theme editor section reload). Call before removing root from DOM.
     */
    destroy() {
      if (!this._isInitialized) return;
      this._unbindEvents();
      this._isInitialized = false;
    }

    /**
     * Full refresh: frequency visibility, card prices, form fields, selected styling.
     * @protected — override in subclass to extend; call super._syncAll()
     */
    _syncAll() {
      const variantId = this._getSelectedVariantId();

      if (this._productLevelHasSellingPlans) {
        if (!this._hasSellingPlansForVariantId(variantId)) {
          this._setSubscriptionOptionDisabled(true);
          const oneTime = this.root.querySelector(SELECTOR.oneTimeInput);
          if (oneTime) oneTime.checked = true;
          this.root.setAttribute('data-purchase-type', PurchaseMode.ONE_TIME);
        } else {
          this._setSubscriptionOptionDisabled(false);
        }

        if (this._getPurchaseType() === PurchaseMode.SUBSCRIPTION && this._hasSellingPlansForVariantId(variantId)) {
          this._rebuildFrequencyOptions(variantId);
        }
      }

      this._updateFrequencyVisibility();
      this._updateCardPrices();
      this._updateForm();
      this._applyVariantSelectionClass();
    }

    /**
     * Hook: runs once after a successful first sync. No-op; override in subclass.
     * @protected
     */
    _afterInit() {}

    // --- Event binding ------------------------------------------------------

    _bindEvents() {
      this._variantIdInput.addEventListener('change', this._onVariantIdInputChange);
      this._purchaseInputs.forEach((input) => {
        input.addEventListener('change', this._onPurchaseTypeChange);
      });
      this.root.querySelectorAll(SELECTOR.variantRadio).forEach((radio) => {
        radio.addEventListener('change', this._onVariantRadioChange);
      });
      if (this._frequencySelect) {
        this._frequencySelect.addEventListener('change', this._onFrequencyChange);
      }
    }

    _unbindEvents() {
      if (this._variantIdInput) {
        this._variantIdInput.removeEventListener('change', this._onVariantIdInputChange);
      }
      if (this._purchaseInputs) {
        this._purchaseInputs.forEach((input) => {
          input.removeEventListener('change', this._onPurchaseTypeChange);
        });
      }
      this.root.querySelectorAll(SELECTOR.variantRadio).forEach((radio) => {
        radio.removeEventListener('change', this._onVariantRadioChange);
      });
      if (this._frequencySelect) {
        this._frequencySelect.removeEventListener('change', this._onFrequencyChange);
      }
    }

    _onVariantIdInputChange() {
      const vid = this._variantIdInput.value;
      if (!vid) return;
      const esc = String(vid).replace(/"/g, '\\"');
      const radio = this.root.querySelector(`input[type="radio"][name^="nw-subscribe-variants-"][value="${esc}"]`);
      if (radio && !radio.checked) {
        radio.checked = true;
      }
      if (!this._hasSellingPlansForVariantId(vid)) {
        const oneTime = this.root.querySelector(SELECTOR.oneTimeInput);
        if (oneTime) oneTime.checked = true;
        this.root.setAttribute('data-purchase-type', PurchaseMode.ONE_TIME);
      }
      this._syncAll();
    }

    _onPurchaseTypeChange() {
      const checked = this.root.querySelector(`${SELECTOR.purchaseTypeInput}:checked`);
      const value = checked ? checked.value : PurchaseMode.ONE_TIME;
      this.root.setAttribute('data-purchase-type', value);
      if (value === PurchaseMode.SUBSCRIPTION && this._hasSellingPlansForVariantId(this._getSelectedVariantId())) {
        this._rebuildFrequencyOptions(this._getSelectedVariantId());
      }
      this._syncAll();
    }

    _onVariantRadioChange() {
      const radio = this.root.querySelector(`${SELECTOR.variantRadio}:checked`);
      if (!radio) return;
      const vid = radio.value;
      if (!this._hasSellingPlansForVariantId(vid) && this._getPurchaseType() === PurchaseMode.SUBSCRIPTION) {
        const oneTime = this.root.querySelector(SELECTOR.oneTimeInput);
        if (oneTime) oneTime.checked = true;
        this.root.setAttribute('data-purchase-type', PurchaseMode.ONE_TIME);
      }
      this._rebuildFrequencyOptions(vid);
      this._syncAll();
    }

    _onFrequencyChange() {
      this._updateCardPrices();
      this._updateForm();
    }

    // --- Data access --------------------------------------------------------

    _getVariantData(variantId) {
      const map = this._config.variants;
      return map && map[String(variantId)] ? map[String(variantId)] : null;
    }

    _getSelectedVariantId() {
      const checked = this.root.querySelector('.nw-subscribe-block-item-variants input[type="radio"]:checked');
      return checked ? checked.value : null;
    }

    _getPurchaseType() {
      const c = this.root.querySelector(`${SELECTOR.purchaseTypeInput}:checked`);
      return c ? c.value : PurchaseMode.ONE_TIME;
    }

    _hasSellingPlansForVariantId(variantId) {
      const v = this._getVariantData(variantId);
      return Boolean(v && v.selling_plans && v.selling_plans.length > 0);
    }

    _getSelectedPlan() {
      const variantId = this._getSelectedVariantId();
      const v = this._getVariantData(variantId);
      if (!v || !v.selling_plans || v.selling_plans.length === 0) return null;
      const id = this._frequencySelect && this._frequencySelect.value
        ? this._frequencySelect.value
        : String(v.selling_plans[0].id);
      return v.selling_plans.find((p) => String(p.id) === id) || v.selling_plans[0];
    }

    // --- UI updates ----------------------------------------------------------

    _setSubscriptionOptionDisabled(disabled) {
      if (this._subscriptionPurchaseInput) {
        this._subscriptionPurchaseInput.disabled = disabled;
        const parent = this._subscriptionPurchaseInput.parentElement;
        if (parent) {
          parent.classList.toggle('is-disabled', disabled);
        }
      }
    }

    _rebuildFrequencyOptions(variantId) {
      if (!this._frequencySelect) return;
      const U = NwSubscribeBlockUtils;
      const v = this._getVariantData(variantId);
      this._frequencySelect.innerHTML = '';
      if (!v || !v.selling_plans || v.selling_plans.length === 0) return;

      let title =
        typeof v.title === 'string'
          ? v.title
          : '';
      const esc = String(variantId).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      const radioForVariant = this.root.querySelector(
        `${SELECTOR.variantRadio}[value="${esc}"]`
      );
      if (radioForVariant?.getAttribute('data-title')) {
        title = radioForVariant.getAttribute('data-title');
      }

      const monthHint = U.inferMonthIntervalFromVariantTitle(title || '');
      let recommendedIndex = 0;
      if (monthHint != null) {
        const idx = U.findSellingPlanIndexForMonths(v.selling_plans, monthHint);
        if (idx >= 0) recommendedIndex = idx;
      }

      v.selling_plans.forEach((plan, i) => {
        const opt = document.createElement('option');
        opt.value = String(plan.id);
        const rec = i === recommendedIndex ? ' (recommended)' : '';
        opt.textContent = `${plan.name}${rec}`;
        this._frequencySelect.appendChild(opt);
      });

      this._frequencySelect.selectedIndex = recommendedIndex;
    }

    _updateCardPrices() {
      const U = NwSubscribeBlockUtils;
      const moneyFormat = this._config.moneyFormat;
      const mode = this._getPurchaseType();
      const selectedId = this._getSelectedVariantId();

      this._variantItems.forEach((item) => {
        const id = item.dataset.variantId;
        const v = this._getVariantData(id);
        if (!v) return;
        const currentEl = item.querySelector('[data-nw-price-current]');
        const compareEl = item.querySelector('[data-nw-price-compare]');
        const saveWrap = item.querySelector('[data-nw-save-wrap]');
        const saveText = item.querySelector('[data-nw-save-text]');
        if (!currentEl) return;

        let showCompare = false;
        let currentCents;
        let compareCents = null;

        if (mode === PurchaseMode.ONE_TIME) {
          currentCents = v.price;
          if (v.compare_at_price && v.compare_at_price > v.price) {
            compareCents = v.compare_at_price;
            showCompare = true;
          }
        } else {
          const p = (() => {
            if (id === selectedId && v.selling_plans && v.selling_plans.length) {
              return this._getSelectedPlan();
            }
            return v.selling_plans && v.selling_plans[0] ? v.selling_plans[0] : null;
          })();
          currentCents = p ? p.price : v.price;
          if (p && p.compare_at_price && p.compare_at_price > p.price) {
            compareCents = p.compare_at_price;
            showCompare = true;
          } else if (!p && v.compare_at_price && v.compare_at_price > v.price) {
            compareCents = v.compare_at_price;
            showCompare = true;
          } else if (p && v.compare_at_price) {
            compareCents = v.compare_at_price;
            showCompare = compareCents > currentCents;
          } else if (v.compare_at_price && v.compare_at_price > currentCents) {
            compareCents = v.compare_at_price;
            showCompare = true;
          }
        }

        const planForSave =
          mode === PurchaseMode.SUBSCRIPTION
            ? id === selectedId
              ? this._getSelectedPlan()
              : v.selling_plans && v.selling_plans[0]
            : null;
        const save = U.getSaveState(mode, v, planForSave);

        currentEl.textContent = U.formatMoney(currentCents, moneyFormat);
        if (compareEl) {
          if (showCompare && compareCents != null) {
            compareEl.textContent = U.formatMoney(compareCents, moneyFormat);
            compareEl.hidden = false;
          } else {
            compareEl.textContent = '';
            compareEl.hidden = true;
          }
        }
        if (saveWrap && saveText) {
          if (save.show) {
            saveText.textContent = save.text;
            saveWrap.hidden = false;
          } else {
            saveWrap.hidden = true;
            saveText.textContent = '';
          }
        }
      });
    }

    _applyVariantSelectionClass() {
      const vid = this._getSelectedVariantId();
      this._variantItems.forEach((el) => {
        el.classList.toggle('is-selected', el.dataset.variantId === vid);
      });
    }

    _updateForm() {
      const variantId = this._getSelectedVariantId();
      const mode = this._getPurchaseType();

      if (variantId) {
        if (String(this._variantIdInput.value) !== String(variantId)) {
          this._variantIdInput.value = variantId;
          this._variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      if (mode === PurchaseMode.ONE_TIME || !this._hasSellingPlansForVariantId(variantId)) {
        this._sellingPlanInput.value = '';
        this._sellingPlanInput.disabled = true;
      } else {
        const plan = this._getSelectedPlan();
        this._sellingPlanInput.disabled = false;
        this._sellingPlanInput.value = plan ? String(plan.id) : '';
      }
    }

    _updateFrequencyVisibility() {
      const mode = this._getPurchaseType();
      const variantId = this._getSelectedVariantId();
      const show = mode === PurchaseMode.SUBSCRIPTION && this._hasSellingPlansForVariantId(variantId);
      if (this._frequencySection) {
        this._frequencySection.hidden = !show;
        this._frequencySection.setAttribute('aria-hidden', show ? 'false' : 'true');
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Bootstrap & global exports (extension points)
  // ---------------------------------------------------------------------------

  function bootstrapNwSubscribeBlocks() {
    const roots = document.querySelectorAll(SELECTOR.root);
    const instances = [];
    roots.forEach((root) => {
      try {
        const block = new NwSubscribeBlock(root);
        block.init();
        // Dev / extension: read controller from DOM
        Object.defineProperty(root, '_nwSubscribeBlock', {
          value: block,
          enumerable: false,
          configurable: true,
        });
        instances.push(block);
      } catch (e) {
        console.error('[NwSubscribeBlock] init failed', e);
      }
    });
    return instances;
  }

  /** @type {typeof NwSubscribeBlock} */
  window.NwSubscribeBlock = NwSubscribeBlock;
  window.NwSubscribeBlockUtils = NwSubscribeBlockUtils;
  window.NwSubscribeBlockBootstrap = bootstrapNwSubscribeBlocks;
  window.NwSubscribeBlockPurchaseMode = PurchaseMode;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrapNwSubscribeBlocks);
  } else {
    bootstrapNwSubscribeBlocks();
  }
})();
